import { useState, useEffect } from "react";
import axios from "axios";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MiniDrawer from "../../../components/MiniDrawer/MiniDrawer";
import { useNavigate } from "react-router-dom";
import ExcelExport from "../../../components/ExcelExport/ExcelExportAccounts";
import Wrapper from "../../../components/UI/Wrapper";

import SalesIcon from "../../../Icons/SalesAmount.png";
import RatioIcon from "../../../Icons/Ratio.png";

const extractYear = (fy) => fy.split(" - ")[0].trim();

const fmt = (val) => {
  const num = Number(val || 0);

  if (num === 0) return "₹ 0";

  return num < 0
    ? `- ₹ ${Math.abs(num).toLocaleString("en-IN")}`
    : `₹ ${num.toLocaleString("en-IN")}`;
};

const fmtSummary = (val) =>
  !val ? "₹ 0" : "₹ " + Number(val).toLocaleString("en-IN");

// Category (service-type) columns coming from the monthly API
const MONTHLY_COLUMNS = [
  { key: "Export Cargo", label: "Export Cargo" },
  { key: "Export NOC", label: "Export NOC" },
  { key: "Import Cargo", label: "Import Cargo" },
  { key: "Import NOC", label: "Import NOC" },
  { key: "Titre Test (EU)", label: "Titre Test (EU)" },
  { key: "Titre Test (India)", label: "Titre Test (India)" },
  { key: "Microchip (Dog)", label: "Microchip (Dog)" },
  { key: "Microchip (Cat)", label: "Microchip (Cat)" },
  { key: "Cage (All Type Size)", label: "Cage (All Type Size)" },
  { key: "Domestic Air", label: "Domestic Air" },
  { key: "Domestic Train", label: "Domestic Train" },
  { key: "Domestic Road", label: "Domestic Road" },
  { key: "Export Baggage", label: "Export Baggage" },
  { key: "None of the Above", label: "None of the Above" },
];

// Roll-up / summary columns coming from the monthly API
const SUMMARY_COLUMNS = [
  { key: "TotalSales", label: "Total Sales" },
  { key: "GrossTotalSales", label: "Gross Total Sales" },
  { key: "DirectExpenses", label: "Direct Expenses" },
  { key: "GrossProfit", label: "Gross Profit" },
  { key: "IndirectExpenses", label: "Indirect Expenses" },
  { key: "NetLoss", label: "Net Loss" },
];

const ALL_NUMERIC_COLUMNS = [...MONTHLY_COLUMNS, ...SUMMARY_COLUMNS];

const ProfitLossReport = () => {
  const navigate = useNavigate();

  const baseAPI = process.env.REACT_APP_API_URL;
  const storedToken = JSON.parse(localStorage.getItem("token"));
  const [year, setYear] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(false);

  const headers = {
    Authorization: `Bearer ${storedToken}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const years = getFinancialYears();

    if (years.includes("2026-2027")) {
      setYear("2026-2027");
    } else {
      const current = new Date().getFullYear();
      setYear(`${current}-${current + 1}`);
    }
  }, []);

  const getFinancialYears = () => {
    const startYear = 2020;
    const currentYear = new Date().getFullYear();

    let years = [];

    for (let i = startYear; i <= currentYear; i++) {
      years.push(`${i}-${i + 1}`);
    }

    return years.reverse(); // latest first
  };

  const fetchReport = async (selectedFy) => {
    setLoading(true);

    try {
      const financialYear = extractYear(selectedFy);

      const [summaryRes, detailRes] = await Promise.all([
        axios.get(
          `${baseAPI}userget/GetPnLReportByServiceTypeKPI?FinancialYear=${financialYear}`,
          { headers },
        ),

        axios.get(
          `${baseAPI}userget/GetPnLReportMonthly?FinancialYear=${financialYear}`,
          { headers },
        ),
      ]);

      setSummary(summaryRes?.data?.data?.[0] || null);

      setRows(detailRes?.data?.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(year);
  }, [year]);

  // Sum every numeric column (category + summary) across all monthly rows
  const totals = rows.reduce((acc, row) => {
    ALL_NUMERIC_COLUMNS.forEach(({ key }) => {
      acc[key] = (acc[key] || 0) + Number(row[key] || 0);
    });
    return acc;
  }, {});

  return (
    <div>
      <div className="app-container-viewLeadDetails">
        <MiniDrawer />

        <div
          style={{ overflowX: "hidden" }}
          className="container-fluid container-viewLeadDetails"
        >
          {/* HEADER */}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
            className="country-header"
          >
            <div>
              <h1 style={{ fontSize: "24px" }}>
                <b>Profit & Loss</b> | <HomeOutlinedIcon /> &gt;
                <a
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/dashboard")}
                >
                  Accounts & Finance
                </a>
                <span style={{ fontSize: "20px" }}> &gt; Reports</span>
                <span style={{ fontSize: "20px" }}> &gt; Profit & Loss</span>
              </h1>
            </div>
          </div>

          {/* FILTERS */}

          <div className="container-fluid px-3 py-2">
            <div
              className="mt-2"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <select value={year} onChange={(e) => setYear(e.target.value)}>
                {getFinancialYears().map((yr, index) => (
                  <option key={index} value={yr}>
                    {yr}
                  </option>
                ))}
              </select>

              <div style={{ display: "flex" }}>
                <ExcelExport />
              </div>
            </div>

            <hr
              style={{
                marginTop: "20px",
                padding: "0px",
                marginBottom: "30px",
              }}
            />

            {/* KPI SECTION */}

            <Wrapper className="p-4">
              <div
                className="p-4"
                style={{
                  backgroundColor: "#5B489D1A",
                  borderRadius: "12px",
                }}
              >
                <div className="row">
                  {/* SALES */}

                  <div className="col-3 px-0">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        style={{
                          width: "55px",
                          height: "55px",
                          padding: "6px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                        className="img-fluid"
                        src={SalesIcon}
                      />

                      <div className="px-2">
                        <h6
                          style={{
                            fontWeight: "bold",
                            color: "#3D3D3D",
                            margin: "0px",
                          }}
                        >
                          Total Sales Amount
                        </h6>

                        <h5
                          style={{
                            color: "#5B489D",
                            fontWeight: "bold",
                          }}
                        >
                          {fmtSummary(summary?.TotalSalesAmount)}
                        </h5>
                      </div>
                    </div>
                  </div>

                  {/* COGS */}

                  <div className="col-3 px-0">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        style={{
                          width: "55px",
                          height: "55px",
                          padding: "6px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                        className="img-fluid"
                        src={SalesIcon}
                      />

                      <div className="px-2">
                        <h6
                          style={{
                            fontWeight: "bold",
                            color: "black",
                            margin: "0px",
                          }}
                        >
                          Total C.O.G.S Amount
                        </h6>

                        <h5
                          style={{
                            color: "#5B489D",
                            fontWeight: "bold",
                          }}
                        >
                          {fmtSummary(summary?.TotalCOGSAmount)}
                        </h5>
                      </div>
                    </div>
                  </div>

                  {/* GP */}

                  <div className="col-3 px-0">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        style={{
                          width: "55px",
                          height: "55px",
                          padding: "6px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                        className="img-fluid"
                        src={SalesIcon}
                      />

                      <div className="px-2">
                        <h6
                          style={{
                            fontWeight: "bold",
                            color: "black",
                            margin: "0px",
                          }}
                        >
                          Total G.P Amount
                        </h6>

                        <h5
                          style={{
                            color: "#5B489D",
                            fontWeight: "bold",
                          }}
                        >
                          {fmtSummary(summary?.TotalGPAmount)}
                        </h5>
                      </div>
                    </div>
                  </div>

                  {/* GP RATIO */}

                  <div className="col-3 px-0">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        style={{
                          width: "55px",
                          height: "55px",
                          padding: "6px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                        className="img-fluid"
                        src={RatioIcon}
                      />

                      <div className="px-2">
                        <h6
                          style={{
                            fontWeight: "bold",
                            color: "black",
                            margin: "0px",
                          }}
                        >
                          Total G.P Ratio
                        </h6>

                        <h5
                          style={{
                            color: "#5B489D",
                            fontWeight: "bold",
                          }}
                        >
                          {summary?.TotalGPRatioPct ?? 0}%
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Wrapper>

            <br />

            {/* TABLE */}

            <div className="expenseList">
              <h2
                className="Venderheading"
                style={{
                  fontSize: "22px",
                  paddingLeft: "16px",
                  paddingTop: "7px",
                }}
              >
                Profit & Loss
              </h2>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{ width: "100%", minWidth: "2400px" }}
                  className="table_expense"
                >
                  <thead>
                    <tr className="expenseListHeading">
                      <th>S.No.</th>
                      <th>Month</th>
                      {MONTHLY_COLUMNS.map((col) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                      {SUMMARY_COLUMNS.map((col) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={2 + ALL_NUMERIC_COLUMNS.length}
                          style={{ textAlign: "center" }}
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length > 0 ? (
                      rows.map((row, index) => (
                        <tr key={index}>
                          <td>{row.SrNo ?? index + 1}</td>

                          <td>{row.Month}</td>

                          {MONTHLY_COLUMNS.map((col) => (
                            <td key={col.key}>{fmt(row[col.key])}</td>
                          ))}

                          {SUMMARY_COLUMNS.map((col) => (
                            <td key={col.key}>{fmt(row[col.key])}</td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={2 + ALL_NUMERIC_COLUMNS.length}
                          style={{ textAlign: "center" }}
                        >
                          No Data Found
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {/* FOOTER */}

                  {rows.length > 0 && (
                    <tfoot>
                      <tr
                        style={{
                          background: "#f3f1fc",
                          fontWeight: "bold",
                        }}
                      >
                        <td colSpan="2">Total</td>

                        {MONTHLY_COLUMNS.map((col) => (
                          <td key={col.key}>{fmt(totals[col.key])}</td>
                        ))}

                        {SUMMARY_COLUMNS.map((col) => (
                          <td key={col.key}>{fmt(totals[col.key])}</td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossReport;
